import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchProjectsByCategory, fetchProjectsByCategories } from '../lib/projects';
import { getFileUrl } from '../lib/pb';
import { MapPin, ArrowRight, Folder, Eye } from 'lucide-react';

export default function ProjectList({ categoryId, categoryIds, sortBy = 'date', onCountChange }){
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    const useMany = Array.isArray(categoryIds) && categoryIds.length;
    const promise = useMany ? fetchProjectsByCategories(categoryIds) : fetchProjectsByCategory(categoryId);
    
    promise
      .then((res) => {
        if(!mounted) return;
        let next = res || [];
        if(sortBy === 'title'){
          next = [...next].sort((a, b) => (a.title || '').localeCompare(b.title || ''));
        }
        setItems(next);
        if(onCountChange) onCountChange(next.length);
      })
      .catch((e) => {
        if(!mounted) return;
        setError(e.message || 'Erreur de chargement');
        if(onCountChange) onCountChange(0);
      })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [categoryId, categoryIds, sortBy, onCountChange]);

  if (loading) return (
    <div className="grid-loading-premium">
      {[1,2,3,4,5,6].map(i => <div key={i} className="skeleton-card"></div>)}
    </div>
  );
  
  if (error) return <div className="error-premium-box">Erreur de chargement : {error}</div>;
  if (!items.length) return <div className="empty-premium-box">Aucune réalisation ne correspond à votre sélection pour le moment.</div>;

  return (
    <div className="project-grid-signature">
      {items.map((p) => (
        <article
          key={p.id}
          className="project-card-premium"
          onClick={() => p.slug && navigate(`/realisations/${p.slug}`)}
        >
          <div className="project-card-visual">
            <div className="project-image-wrapper">
              {Array.isArray(p.gallery) && p.gallery[0] && (
                <img alt={p.title} src={getFileUrl(p, p.gallery[0])} loading="lazy" />
              )}
              {(!p.gallery || !p.gallery[0]) && <div className="img-placeholder"><Folder size={40}/></div>}
            </div>
            
            <div className="project-overlay-premium">
               <div className="overlay-btn-circle">
                  <Eye size={24} />
               </div>
               <span className="overlay-text-btn">Voir le projet</span>
            </div>

            <div className="project-badge-cat">
               {p.expand?.category?.name || 'Réalisation'}
            </div>
          </div>

          <div className="project-card-content">
            <h3 className="project-card-title">{p.title}</h3>
            {p.location && (
              <div className="project-card-loc">
                <MapPin size={14} />
                <span>{p.location}</span>
              </div>
            )}
            <div className="project-card-footer">
               <div className="explore-line">Explorer l'œuvre <ArrowRight size={14}/></div>
            </div>
          </div>
        </article>
      ))}

      <style jsx>{`
        .project-grid-signature { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 30px; }
        
        .project-card-premium { 
          background: #fff; border-radius: 32px; overflow: hidden; cursor: pointer; 
          transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1); border: 1px solid #f1f5f9; box-shadow: 0 10px 40px -15px rgba(15,23,42,0.06);
          display: flex; flex-direction: column;
        }
        .project-card-premium:hover { transform: translateY(-8px); box-shadow: 0 30px 60px -20px rgba(15,23,42,0.15); border-color: var(--accent); }

        .project-card-visual { position: relative; width: 100%; aspect-ratio: 4/3; overflow: hidden; background: #f8fafc; }
        .project-image-wrapper { width: 100%; height: 100%; transition: all 0.7s cubic-bezier(0.16, 1, 0.3, 1); }
        .project-image-wrapper img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.8s ease; }
        .project-card-premium:hover .project-image-wrapper img { transform: scale(1.1); }

        .project-overlay-premium { 
          position: absolute; inset: 0; background: rgba(15, 23, 42, 0.5); opacity: 0; display: flex; flex-direction: column; 
          align-items: center; justify-content: center; gap: 15px; transition: all 0.4s; z-index: 5;
        }
        .overlay-btn-circle { width: 60px; height: 60px; border-radius: 50%; background: var(--accent); color: #fff; display: flex; align-items: center; justify-content: center; transform: translateY(20px); transition: all 0.4s 0.1s; }
        .overlay-text-btn { color: #fff; font-size: 14px; font-weight: 800; text-transform: uppercase; letter-spacing: 1px; transform: translateY(20px); transition: all 0.4s 0.15s; }
        
        .project-card-premium:hover .project-overlay-premium { opacity: 1; }
        .project-card-premium:hover .overlay-btn-circle, .project-card-premium:hover .overlay-text-btn { transform: translateY(0); }

        .project-badge-cat { 
          position: absolute; top: 20px; left: 20px; padding: 6px 16px; background: rgba(255,255,255,0.9); backdrop-filter: blur(10px); 
          border-radius: 99px; font-size: 11px; font-weight: 800; color: #0f172a; text-transform: uppercase; letter-spacing: 0.5px; z-index: 10;
        }

        .project-card-content { padding: 25px; flex: 1; display: flex; flex-direction: column; gap: 8px; }
        .project-card-title { font-size: 19px; font-weight: 900; color: #0f172a; margin: 0; line-height: 1.3; }
        .project-card-loc { display: flex; align-items: center; gap: 6px; color: #64748b; font-size: 14px; font-weight: 600; }
        
        .project-card-footer { margin-top: auto; padding-top: 15px; border-top: 1px solid #f1f5f9; }
        .explore-line { display: flex; align-items: center; gap: 8px; color: var(--accent); font-size: 12px; font-weight: 800; text-transform: uppercase; transition: all 0.2s; }
        .project-card-premium:hover .explore-line { gap: 12px; }

        /* LOADING & ERROR */
        .grid-loading-premium { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 30px; }
        .skeleton-card { aspect-ratio: 4/3; background: linear-gradient(90deg, #f1f5f9 25%, #f8fafc 50%, #f1f5f9 75%); background-size: 200% 100%; animation: skeletonLoad 1.5s infinite; border-radius: 32px; }
        @keyframes skeletonLoad { from { background-position: 200% 0; } to { background-position: -200% 0; } }

        .error-premium-box, .empty-premium-box { grid-column: 1 / -1; padding: 80px 40px; background: #fff; border-radius: 32px; border: 1px dashed #e2e8f0; text-align: center; color: #64748b; font-size: 16px; font-weight: 600; }

        @media (max-width: 600px) {
          .project-grid-signature { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
}
